/**
 * qualityView.ts — pure view-model helpers for Operations / Quality Checks
 * (M2).
 *
 * The wire stores result as a lowercase slug (0100's CHECK); this module owns
 * the display labels — the mockup modal's Pass/Fail options
 * (klaxiora-crm-mockup-full.html :3195-3198) — the header count line
 * (:5250), and the three KPI computations (:5243-5257): checks this month,
 * pass rate across all checks, failed-and-open. Unlike the mockup's
 * "this month" KPI (which counts every check, :5252-5253), thisMonth here is
 * genuinely month-scoped — the label must not lie about the number.
 */
import type { OpsQualityCheck } from '../../types'

export const QC_RESULTS = [
  { slug: 'pass', label: 'Pass' },
  { slug: 'fail', label: 'Fail' },
] as const

export function qcResultLabel(slug: string): string {
  return QC_RESULTS.find(r => r.slug === slug)?.label ?? slug
}

/** The header count line — the mockup's renderQualityChecksList wording
 *  (:5250); the static default subtitle belongs to the page (:1989). */
export function qcCountLine(n: number): string {
  return `${n} check${n !== 1 ? 's' : ''} logged.`
}

export interface QcKpis {
  thisMonth: number
  /** Rounded percent across ALL checks; null when there are none (render
   *  '—', never a fabricated 0%). */
  passRate: number | null
  failedOpen: number
}

export function qcKpis(checks: OpsQualityCheck[], now: Date): QcKpis {
  const thisMonth = checks.filter(c => {
    const d = new Date(c.createdAt)
    return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth()
  }).length
  const passCount = checks.filter(c => c.result === 'pass').length
  const passRate = checks.length ? Math.round((passCount / checks.length) * 100) : null
  const failedOpen = checks.filter(c => c.result === 'fail' && !c.resolved).length
  return { thisMonth, passRate, failedOpen }
}
